import type { ActivityMaterial, ActivityWrite, UploadFile } from "@learn-flow/contracts";

export type ActivityDraftUploadMaterial = {
  id: string;
  kind: "image" | "file";
  fileId: string | null;
  file: UploadFile | null;
  altText: string;
  decorative: boolean;
  uploadStatus: "empty" | "uploading" | "ready" | "failed";
  uploadError: string;
};

export type ActivityDraftMaterial =
  | Exclude<ActivityMaterial, { kind: "image" | "file" }>
  | ActivityDraftUploadMaterial;

export type ActivityDraft = {
  key: string;
  baseVersion: number | null;
  idempotencyKey: string;
  scheduledCreation?: {
    activityId: string;
    title: string;
    schedulingIdempotencyKey: string;
  };
  fields: Omit<ActivityWrite, "materials">;
  materials: ActivityDraftMaterial[];
  savedAt: string;
};

const DATABASE_NAME = "learn-flow-drafts";
const STORE_NAME = "activity-drafts";

export async function readActivityDraft(key: string): Promise<ActivityDraft | null> {
  const database = await openDraftDatabase();
  return new Promise((resolve, reject) => {
    const request = database.transaction(STORE_NAME).objectStore(STORE_NAME).get(key);
    request.addEventListener("success", () => {
      resolve(isActivityDraft(request.result) ? request.result : null);
      database.close();
    });
    request.addEventListener("error", () => {
      reject(request.error ?? new Error("Draft could not be read."));
      database.close();
    });
  });
}

export async function writeActivityDraft(draft: ActivityDraft): Promise<void> {
  const database = await openDraftDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    transaction.objectStore(STORE_NAME).put(draft);
    transaction.addEventListener("complete", () => {
      resolve();
      database.close();
    });
    transaction.addEventListener("error", () => {
      reject(transaction.error ?? new Error("Draft could not be saved."));
      database.close();
    });
  });
}

export async function deleteActivityDraft(key: string): Promise<void> {
  const database = await openDraftDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    transaction.objectStore(STORE_NAME).delete(key);
    transaction.addEventListener("complete", () => {
      resolve();
      database.close();
    });
    transaction.addEventListener("error", () => {
      reject(transaction.error ?? new Error("Draft could not be removed."));
      database.close();
    });
  });
}

function openDraftDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, 1);
    request.addEventListener("upgradeneeded", () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME, { keyPath: "key" });
      }
    });
    request.addEventListener("success", () => resolve(request.result));
    request.addEventListener("error", () =>
      reject(request.error ?? new Error("Draft storage could not be opened.")),
    );
  });
}

function isActivityDraft(value: unknown): value is ActivityDraft {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Partial<ActivityDraft>;
  return (
    typeof candidate.key === "string" &&
    (candidate.baseVersion === null || typeof candidate.baseVersion === "number") &&
    typeof candidate.idempotencyKey === "string" &&
    (candidate.scheduledCreation === undefined ||
      (typeof candidate.scheduledCreation === "object" &&
        candidate.scheduledCreation !== null &&
        typeof candidate.scheduledCreation.activityId === "string" &&
        typeof candidate.scheduledCreation.title === "string" &&
        typeof candidate.scheduledCreation.schedulingIdempotencyKey === "string")) &&
    typeof candidate.fields === "object" &&
    candidate.fields !== null &&
    Array.isArray(candidate.materials) &&
    typeof candidate.savedAt === "string"
  );
}
